import type { Edge, Node } from '@xyflow/react'
import { useFlowStore } from '../../store'
import {
  FlowNodeTypes,
  type GenericEdge,
  type GenericNode,
  type GlobalMeta,
  type NodeDataBase,
} from '../../types'
import { leaderNodesForSave } from '../../utils/system-one-thinking'

interface SerializeVibeAgentWorkflowParams {
  nodes: GenericNode[]
  edges: GenericEdge[]
  baseGlobalMeta?: GlobalMeta
}

const hasGlobalMetaValue = (globalMeta: Record<string, unknown>) =>
  Object.values(globalMeta).some((value) => value !== undefined && value !== null)

const buildGlobalMeta = (
  baseGlobalMeta: GlobalMeta | undefined,
): Record<string, unknown> | undefined => {
  const baseMeta = baseGlobalMeta || undefined
  const { systemPrompt, isGlobalMetaLoaded, sttGuidance, agentName, agentPersona } =
    useFlowStore.getState()
  const shouldFallbackToBaseMeta = !!baseMeta && !isGlobalMetaLoaded

  const globalMeta: Record<string, unknown> = {
    ...(baseMeta || {}),
    system_prompt: systemPrompt
      ? {
          prompt: systemPrompt.prompt,
          all_nodes_enabled: systemPrompt.all_nodes_enabled !== false,
          ai_nodes: systemPrompt.ai_nodes || [],
        }
      : shouldFallbackToBaseMeta
        ? baseMeta?.system_prompt
        : null,
    stt_guidance: sttGuidance ?? (shouldFallbackToBaseMeta ? baseMeta?.stt_guidance : null),
    agent_name: agentName ?? (shouldFallbackToBaseMeta ? baseMeta?.agent_name : null),
    agent_persona: agentPersona ?? (shouldFallbackToBaseMeta ? baseMeta?.agent_persona : null),
  }

  if (!baseMeta && !hasGlobalMetaValue(globalMeta)) return undefined

  return globalMeta
}

export const serializeVibeAgentWorkflow = ({
  nodes,
  edges,
  baseGlobalMeta,
}: SerializeVibeAgentWorkflowParams): string => {
  const filteredNodes = nodes.filter((node) => node.type !== FlowNodeTypes.select_trigger)
  const globalMeta = buildGlobalMeta(baseGlobalMeta)
  const workflow: {
    nodes: Node<NodeDataBase<unknown>>[]
    edges: Edge[]
    global_meta?: Record<string, unknown>
  } = {
    nodes:
      leaderNodesForSave(filteredNodes, useFlowStore.getState().leaderProfile) ?? filteredNodes,
    edges,
  }

  if (globalMeta) {
    workflow.global_meta = globalMeta
  }

  return JSON.stringify(workflow)
}
